import React from 'react';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { Button } from 'antd';

import {
  getNameKeyByResource,
  getPluralSuffix,
  isDeleteAction,
  isUndeployAction,
} from '@src/emp-3.0/utils/management';
import { CUSTOM_DIMENSION, FLOW, MAPPED_EVENTS, METRICS } from '@src/emp-3.0/constants/management';

import {
  NameItem,
  NameList,
  RequestCompletionPromptContent,
  RequestCompletionPromptWrapper,
} from './styled';
import { IPromptProps } from './typed';

function getActionMessage(
  isSuccess: boolean,
  actionType: string | null,
  count: number,
  resource: string,
): string {
  const getResourceType = () => {
    switch (resource) {
      case FLOW:
        return count <= 1 ? 'flow' : 'flows';
      case METRICS:
        return count <= 1 ? 'metric' : 'metrics';
      case MAPPED_EVENTS:
        return count <= 1 ? 'mapped event' : 'mapped events';
      case CUSTOM_DIMENSION:
        return count <= 1 ? 'custom dimension' : 'custom dimensions';
      default:
        return resource;
    }
  };

  return isSuccess
    ? `${actionType} ${count} ${getResourceType()} successfully!`
    : `Failed to ${actionType} ${count} ${getResourceType()}!`;
}

const RequestCompletionPrompt: React.FC<IPromptProps> = ({
  data = [],
  resource,
  actionType,
  requestStatus,
  onTryAgain,
  onCancel,
}) => {
  const count = data.length;
  const isUndeployedAction = isUndeployAction(actionType);
  const isDeletedAction = isDeleteAction(actionType);
  const isSuccess = requestStatus === 'success';

  const getStatusStyles = () => {
    if (isSuccess) {
      return { color: '#52C41A', background: '#F6FFED' };
    }
    return { color: '#F5222D', background: '#FFF1F0' };
  };

  const renderActionMessage = () => {
    if (isSuccess) {
      if (isUndeployedAction) {
        return (
          <NameItem>
            The following item{getPluralSuffix(count)} are no longer available on Pulse
          </NameItem>
        );
      }
      if (isDeletedAction) {
        return <NameItem>The following are deleted from Activation.</NameItem>;
      }
    }
    return null;
  };

  const renderNameList = () =>
    data?.map((item, index) => (
      <NameItem key={`${item.id}_${index}`}>
        {isSuccess ? <CheckOutlined /> : <CloseOutlined />}
        <span>{item[getNameKeyByResource(resource)]}</span>
      </NameItem>
    ));

  const renderButtons = () => {
    if (isSuccess) {
      return (
        <Button type="primary" onClick={onCancel}>
          Done
        </Button>
      );
    }
    return (
      <>
        <Button type="primary" onClick={onTryAgain} style={{ marginRight: 8 }}>
          Go Back and Try Again
        </Button>
        <Button onClick={onCancel}>Cancel</Button>
      </>
    );
  };

  return (
    <>
      <RequestCompletionPromptWrapper>
        <RequestCompletionPromptContent style={{ background: getStatusStyles().background }}>
          <NameItem
            style={{
              color: getStatusStyles().color,
              fontWeight: 600,
              fontSize: 16,
            }}
          >
            {getActionMessage(isSuccess, actionType, count, resource)}
          </NameItem>
          {renderActionMessage()}
          <NameList>{renderNameList()}</NameList>
        </RequestCompletionPromptContent>
      </RequestCompletionPromptWrapper>
      <div style={{ textAlign: 'end', marginRight: 24 }}>{renderButtons()}</div>
    </>
  );
};

export default RequestCompletionPrompt;
